import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config/config";

const SVC = `${baseUrl}/job-posting`;

function errorMessage(code) {
  switch (code) {
    case "invalid_token":
      return "This link is not valid or has already been used.";
    case "invite_expired":
      return "This interview link has expired.";
    case "invite_canceled":
      return "This interview was canceled.";
    case "invite_not_yet_valid":
      return "Your interview window has not opened yet.";
    case "agent_unavailable":
      return "We couldn't reach the interview service.Please try again in a moment.";
    default:
      return "Please try again, or contact the recruiter if the problem persists.";
  }
}

export default function CandidateJoinPage() {
  const { inviteToken } = useParams();
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);
  const [joinUrl, setJoinUrl] = useState(null);
  // idle | requesting | granted | denied
  const [perm, setPerm] = useState("idle");
  const [permError, setPermError] = useState(null);
  const fired = useRef(false);

  useEffect(() => {
    document.title = "Joining Interview — Ample Hire";
  }, []);

  useEffect(() => {
    if (fired.current || !inviteToken) return;
    fired.current = true;
    join();
  }, [inviteToken]);

  // Once permissions are granted AND we have the join URL, redirect.
  useEffect(() => {
    if (perm === "granted" && joinUrl) {
      window.location.replace(joinUrl);
    }
  }, [perm, joinUrl]);

  async function join() {
    setErr(null);
    setBusy(true);
    try {
      const r = await axios.post(`${SVC}/api/public/interview/join`, {
        inviteToken,
      });
      const data = r.data;
      if (data.join_url) {
        // Don't redirect yet — gate on camera/mic permission first.
        setJoinUrl(data.join_url);
        setBusy(false);
      } else if (data.error === "agent_unavailable") {
        setErr(data.error);
      }
    } catch (e) {
      const code =
        e.response?.data?.error || `http_${e.response?.status || "error"}`;
      setErr(code);
      fired.current = false;
      setBusy(false);
    }
  }

  async function requestPermissions() {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPerm("denied");
      setPermError("unsupported");
      return;
    }
    setPerm("requesting");
    setPermError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });
      // Release the tracks — LiveKit will re-acquire them when the page loads.
      stream.getTracks().forEach((t) => t.stop());
      setPerm("granted");
    } catch (e) {
      setPerm("denied");
      setPermError(e?.name || "PermissionDenied");
    }
  }

  function permErrorMessage(name) {
    switch (name) {
      case "NotAllowedError":
        return "Permission was blocked. Click the camera icon in your browser's address bar to allow access, then try again.";
      case "NotFoundError":
      case "OverconstrainedError":
        return "No camera or microphone was found on this device. Plug one in and try again.";
      case "NotReadableError":
        return "Your camera or microphone is being used by another app. Close it and try again.";
      case "SecurityError":
        return "Your browser blocked access for security reasons. Make sure this page is loaded over HTTPS.";
      case "unsupported":
        return "Your browser doesn't support camera/microphone access. Try Chrome, Edge, or Firefox on a desktop.";
      default:
        return "We couldn't access your camera or microphone. Please try again.";
    }
  }

  if (err) {
    return (
      <div className="min-h-screen bg-vf-bg flex items-center justify-center p-4">
        <div className="card max-w-md w-full p-8 text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto text-xl font-bold">
            !
          </div>
          <h2 className="font-semibold text-lg text-vf-base">
            Couldn't connect to the interview room.
          </h2>
          <p className="text-sm text-vf-muted">{errorMessage(err)}</p>
          <button
            onClick={join}
            disabled={busy}
            className="btn-primary inline-block disabled:opacity-50"
          >
            {busy ? "Retrying…" : "Try again"}
          </button>
        </div>
      </div>
    );
  }

  // We have a join URL but still need camera/mic permission.
  if (joinUrl && perm !== "granted") {
    const denied = perm === "denied";
    const requesting = perm === "requesting";
    return (
      <div className="min-h-screen bg-vf-bg flex items-center justify-center p-4">
        <div className="card max-w-md w-full p-8 space-y-5">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto ${
              denied
                ? "bg-red-50 text-red-500"
                : "bg-vf-primary/10 text-vf-primary"
            }`}
          >
            {/* Camera + mic glyph */}
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="6" width="14" height="12" rx="2" />
              <path d="m22 8-6 4 6 4V8Z" />
            </svg>
          </div>

          <div className="text-center space-y-2">
            <h2 className="font-semibold text-lg text-vf-base">
              {denied
                ? "Camera & microphone blocked"
                : "Enable camera & microphone"}
            </h2>
            <p className="text-sm text-vf-muted">
              {denied
                ? permErrorMessage(permError)
                : "We need access to your camera and microphone so your interviewer can see and hear you. Your browser will ask you to allow this."}
            </p>
          </div>

          {!denied && (
            <ul className="text-xs text-vf-muted space-y-1.5 bg-vf-bg rounded-md p-3">
              <li className="flex items-start gap-2">
                <span className="text-vf-primary mt-0.5">●</span>
                We don't record before you join the room.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-vf-primary mt-0.5">●</span>
                You can mute or turn off your camera anytime during the
                interview.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-vf-primary mt-0.5">●</span>
                Use Chrome, Edge, or Firefox on a desktop for best results.
              </li>
            </ul>
          )}

          <button
            onClick={requestPermissions}
            disabled={requesting}
            className="btn-primary w-full disabled:opacity-50"
          >
            {requesting
              ? "Waiting for browser…"
              : denied
                ? "Try again"
                : "Enable camera & microphone"}
          </button>

          {denied && (
            <p className="text-[11px] text-vf-muted text-center">
              Still blocked? Click the camera icon next to the URL bar → allow
              camera & microphone → refresh this page.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vf-bg flex items-center justify-center p-4">
      <div className="card max-w-md w-full p-10 text-center space-y-4">
        <div className="w-10 h-10 border-2 border-vf-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <h2 className="font-semibold text-lg text-vf-base">
          {perm === "granted" ? "Connecting…" : "Joining your interview…"}
        </h2>
        <p className="text-sm text-vf-muted">
          {perm === "granted"
            ? "Redirecting you to the interview room."
            : "Preparing your room. This will take a moment."}
        </p>
      </div>
    </div>
  );
}
